/* ============================================================
   METAL FINGERS - SELECTION DRAWER

   One reusable right-side drawer for choosing a product fast, used
   by every part of the range:

     new cartridges   gauge, grouping and taper, with the education
     OG cartridges    same option structure, different stock
     merch            whatever options that product actually has
     one-variant      never opens: it adds straight to the cart

   Opens from:  the WHAT ARE YOU RUNNING? tiles
                the Shop All tiles
                the OG Collection and Merch cards
                anything carrying data-mf-drawer="product-handle"

   Data:        live Shopify product JSON (/products/<handle>.js), so
                price, options and availability follow the customer's
                market automatically and are never hardcoded.

   Layering:    adding to the cart does NOT close this drawer. The
                Smart Cart slides over it and the drawer waits
                underneath with the configuration intact, so an artist
                building a multi-configuration order can close the
                cart and carry straight on.

   Everything configurable lives in mf-needle-config.js.
   ============================================================ */

(function () {
  'use strict';

  if (window.MFNeedleDrawer) return;

  var ROOT = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
  var CFG = window.MFNeedleConfig || {};
  var NOT_CARRIED = CFG.notCarried || [];
  var PDP_VIEW = CFG.pdpView || {};
  var GAUGES = CFG.gaugeReference || [];
  var TAPERS = CFG.taperReference || [];
  var PROOF = CFG.proof || [];
  var UNIT = CFG.cartridgeUnit || '';
  var CLEARANCE = CFG.clearanceNote || '';

  /* The quantity a customer may type into the box. The ceiling is the
     same 99 the Smart Cart line quantity is set to, so a number that
     is accepted here cannot be rejected a second later in the cart. */
  /* ---------- How much screen there actually is ----------

     Three separate things make the bottom of a phone screen a lie,
     and the purchase footer lives at the bottom.

     One: iOS Safari sizes a fixed, full-height box to the large
     viewport, the height the page would have with the browser
     toolbars retracted. With the toolbars showing, the bottom of that
     box is behind them. 100dvh in the stylesheet answers that on its
     own in a current browser and is the standing fix.

     Two: the keyboard. dvh does not shrink for it, so somebody typing
     a quantity would push Add to cart underneath the keyboard.
     visualViewport does shrink, so that is what is measured here.

     Three: anything pinned over the bottom of the page. The Shopify
     theme preview bar and Rebuy's cart preview bar both sit there,
     both at or above this drawer in the stacking order, and both
     cover the purchase footer. They belong to preview and a customer
     never sees them, so nothing about them is hard coded: whatever is
     actually pinned over the bottom is measured and subtracted, and
     when nothing is there this subtracts nothing.

     The result is written straight onto the drawer as a height. No
     margin is invented, and no guess is made about how tall any of
     it is. */

  var vpBound = false;

  function bottomOverlayHeight() {
    if (!document.body) return 0;
    var kids = document.body.children, worst = 0, vh = window.innerHeight;
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (el.id === 'MFNeedleDrawer') continue;
      var cs;
      try { cs = window.getComputedStyle(el); } catch (e) { continue; }
      if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') continue;
      var r = el.getBoundingClientRect();
      if (r.height < 8 || r.width < window.innerWidth * 0.5) continue;
      /* Only things that reach the bottom edge, and only if they are a
         bar rather than a full-screen layer. */
      if (r.bottom < vh - 4) continue;
      var reach = vh - r.top;
      if (reach > 0 && reach < vh * 0.5 && reach > worst) worst = reach;
    }
    return Math.round(worst);
  }

  function sizeToViewport() {
    var r = Drawer.root;
    if (!r || !r.hasAttribute('data-open')) return;
    var vv = window.visualViewport;
    var visible = vv ? vv.height : window.innerHeight;
    var usable = Math.min(visible, window.innerHeight - bottomOverlayHeight());
    if (!(usable > 0)) usable = window.innerHeight;
    if (usable < 320) usable = 320;
    r.style.height = Math.round(usable) + 'px';
    /* With the keyboard up, iOS can offset the visual viewport from the
       layout viewport a fixed element is positioned against. */
    r.style.top = vv && vv.offsetTop ? Math.round(vv.offsetTop) + 'px' : '';
  }

  function releaseViewport() {
    var r = Drawer.root;
    if (!r) return;
    r.style.height = '';
    r.style.top = '';
  }

  function bindViewport() {
    if (vpBound) return;
    vpBound = true;
    var vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', sizeToViewport);
      vv.addEventListener('scroll', sizeToViewport);
    }
    window.addEventListener('resize', sizeToViewport);
    window.addEventListener('orientationchange', function () {
      setTimeout(sizeToViewport, 240);
    });
  }

  var MIN_QTY = 1;
  var MAX_QTY = 99;

  /* Clearance products are the OG Collection, and that list lives in
     exactly one place: mf-cart-config.js. Read at call time rather
     than at parse time, because the two config files are loaded by
     two different sections. */
  function isClearance(handle) {
    var list = (window.MFCartConfig && window.MFCartConfig.ogHandles) || [];
    return list.indexOf(handle) !== -1;
  }

  /* ---------- helpers ---------- */

  function money(cents) {
    var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: code, currencyDisplay: 'narrowSymbol'
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function currencyCode() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
  }

  function optionNames(product) {
    return (product.options || []).map(function (o) {
      return typeof o === 'string' ? o : o.name;
    });
  }

  function indexOfOption(names, wanted) {
    for (var i = 0; i < names.length; i++) {
      if (String(names[i]).trim().toLowerCase() === wanted) return i;
    }
    return -1;
  }

  function variantOption(variant, i) {
    if (variant.options && variant.options.length > i) return variant.options[i];
    return variant['option' + (i + 1)];
  }

  function uniq(list) {
    var seen = {}, out = [];
    list.forEach(function (v) { if (v != null && !seen[v]) { seen[v] = 1; out.push(v); } });
    return out;
  }

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* Options are offered in number order, not in the order the variants
     happen to sit in Shopify. A configuration added later - 9, 11 and 7
     on the OG Curved Magnums - was landing on the end of the row, so
     the groupings read 13, 15, 17 ... 27, 9, 11, 7. Anything that is
     not a number falls back to comparing as text, so a family that
     ever uses a word for a grouping is left alone rather than
     scrambled. Ordering is per option value and identical in every
     market: nothing here reads price, currency or locale. */
  function byNumber(a, b) {
    var x = parseFloat(a), y = parseFloat(b);
    var xn = !isNaN(x) && isFinite(x), yn = !isNaN(y) && isFinite(y);
    if (xn && yn) return x - y;
    if (xn) return -1;
    if (yn) return 1;
    return String(a).localeCompare(String(b));
  }

  /* Tapers run short to long - M, L, XL - which is the order the
     education below them is drawn in. */
  function taperRank(value) {
    var k = taperKey(value);
    for (var i = 0; i < TAPERS.length; i++) {
      if (TAPERS[i].key === k) return i;
    }
    return 99;
  }

  /* "XL (7.0mm)" -> "XL". The millimetres are taught by the taper
     reference strip, so they are not repeated in the config line. */
  function taperKey(value) {
    return String(value || '').split('(')[0].trim().toUpperCase();
  }

  /* Gauges are always written as two digits: 06, 08, 10, 12.
     Shopify stores them as 8, 10, 12, so the raw option value stays
     the data and this is only ever what the customer reads. */
  function gaugeLabel(value) {
    var n = parseInt(value, 10);
    if (isNaN(n)) return String(value);
    return n < 10 ? '0' + n : String(n);
  }

  function gaugeMm(value) {
    var n = parseInt(value, 10);
    for (var i = 0; i < GAUGES.length; i++) {
      if (parseInt(GAUGES[i].label, 10) === n) return GAUGES[i].mm || '';
    }
    return '';
  }

  /* A product whose only option is Shopify's placeholder, or which has
     one variant and nothing to choose. These never open the drawer. */
  function nothingToChoose(product, pool) {
    if (pool.length <= 1) return true;
    var names = optionNames(product);
    for (var i = 0; i < names.length; i++) {
      if (String(names[i]).trim().toLowerCase() === 'title') continue;
      if (uniq(pool.map(function (v) { return variantOption(v, i); })).length > 1) return false;
    }
    return true;
  }

  /* ---------- controller ---------- */

  var Drawer = {
    root: null, panel: null, ui: null, cache: {}, inflight: {},
    product: null, meta: null, mode: 'cartridge',
    idx: { gauge: -1, grouping: -1, taper: -1 },
    simpleIdx: [],
    sel: { gauge: null, grouping: null, taper: null, simple: {} },
    busy: false, lastFocus: null, under: false,

    build: function () {
      if (this.root) return;

      var r = document.createElement('div');
      r.id = 'MFNeedleDrawer';
      r.setAttribute('role', 'dialog');
      r.setAttribute('aria-modal', 'true');
      r.setAttribute('aria-label', 'Choose your options');

      r.innerHTML =
        '<div class="mf-nd__scrim" data-mf-nd-close></div>' +
        '<div class="mf-nd__panel">' +
          '<div class="mf-nd__head">' +
            '<div>' +
              '<p class="mf-nd__eyebrow"><span class="mf-nd__code" data-nd-code hidden></span><span data-nd-strapline></span></p>' +
              '<h2 class="mf-nd__title" data-nd-title></h2>' +
            '</div>' +
            '<button type="button" class="mf-nd__close" data-mf-nd-close aria-label="Close">' +
              '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>' +
            '</button>' +
          '</div>' +

          '<div class="mf-nd__body">' +
            '<p class="mf-nd__state" data-nd-loading>Loading options</p>' +
            '<p class="mf-nd__state" data-nd-error hidden>We could not load this one just now.</p>' +

            '<div data-nd-main hidden>' +

              '<div class="mf-nd__hero">' +
                '<span class="mf-nd__shot"><img alt="" data-nd-image></span>' +
                '<span class="mf-nd__pricing">' +
                  '<span class="mf-nd__price"><span data-nd-price></span><span class="mf-nd__currency" data-nd-currency></span></span>' +
                  '<span class="mf-nd__unit" data-nd-unit hidden></span>' +
                  '<a class="mf-nd__full" data-nd-full href="#">View full product details</a>' +
                  '<p class="mf-nd__clear" data-nd-clear hidden></p>' +
                '</span>' +
              '</div>' +

              '<div data-nd-cartridge hidden>' +
                '<div class="mf-nd__group mf-nd__group--split" data-nd-gauge-group hidden>' +
                  '<div class="mf-nd__control">' +
                    '<p class="mf-nd__label"><span>Gauge</span></p>' +
                    '<div class="mf-nd__chips" data-nd-gauge role="group" aria-label="Gauge"></div>' +
                    '<p class="mf-nd__fixed" data-nd-gauge-fixed hidden></p>' +
                  '</div>' +
                  '<div class="mf-nd__ref" data-nd-gauge-ref aria-hidden="true">' +
                    '<p class="mf-nd__ref-label">Needle diameter</p>' +
                    '<div class="mf-nd__dots" data-nd-gauge-dots></div>' +
                  '</div>' +
                '</div>' +

                '<div class="mf-nd__group">' +
                  '<p class="mf-nd__label"><span>Grouping</span></p>' +
                  '<div class="mf-nd__chips" data-nd-grouping role="group" aria-label="Grouping"></div>' +
                  '<p class="mf-nd__note" data-nd-oos hidden>Crossed out options are out of stock.</p>' +
                '</div>' +

                '<div class="mf-nd__group" data-nd-taper-group>' +
                  '<p class="mf-nd__label"><span>Taper</span><span class="mf-nd__label-hint">How far back the needle starts narrowing</span></p>' +
                  '<div class="mf-nd__chips mf-nd__chips--taper" data-nd-taper-chips role="group" aria-label="Taper" hidden></div>' +
                  '<div class="mf-nd__tapers" data-nd-tapers aria-hidden="true"></div>' +
                '</div>' +

                '<p class="mf-nd__config"><span>Config</span><b data-nd-config>-</b></p>' +

                '<div class="mf-nd__proof">' +
                  '<ul class="mf-nd__proof-list" data-nd-proof></ul>' +
                '</div>' +
              '</div>' +

              '<div data-nd-simple hidden></div>' +

            '</div>' +
          '</div>' +

          '<div class="mf-nd__foot">' +
            '<span class="mf-nd__qty-field">' +
              '<span class="mf-nd__qty-label" data-nd-qty-label>Qty</span>' +
              '<span class="mf-nd__qty">' +
                '<button type="button" data-nd-minus aria-label="Decrease quantity">−</button>' +
                '<input type="text" data-nd-qty value="1" inputmode="numeric" pattern="[0-9]*" autocomplete="off" enterkeyhint="done" aria-label="Quantity">' +
                '<button type="button" data-nd-plus aria-label="Increase quantity">+</button>' +
              '</span>' +
            '</span>' +
            '<button type="button" class="mf-nd__add" data-nd-add disabled>Add to cart</button>' +
          '</div>' +

          '<span class="mf-nd__live" role="status" aria-live="polite" data-nd-live></span>' +
        '</div>';

      document.body.appendChild(r);
      this.root = r;
      this.panel = r.querySelector('.mf-nd__panel');

      var q = function (s) { return r.querySelector(s); };
      this.ui = {
        code: q('[data-nd-code]'), strapline: q('[data-nd-strapline]'), title: q('[data-nd-title]'),
        loading: q('[data-nd-loading]'), error: q('[data-nd-error]'), main: q('[data-nd-main]'),
        image: q('[data-nd-image]'), price: q('[data-nd-price]'), currency: q('[data-nd-currency]'),
        unit: q('[data-nd-unit]'), full: q('[data-nd-full]'), clear: q('[data-nd-clear]'),
        cartridge: q('[data-nd-cartridge]'), simple: q('[data-nd-simple]'),
        gaugeGroup: q('[data-nd-gauge-group]'), gauge: q('[data-nd-gauge]'),
        gaugeFixed: q('[data-nd-gauge-fixed]'), gaugeDots: q('[data-nd-gauge-dots]'),
        grouping: q('[data-nd-grouping]'), oos: q('[data-nd-oos]'),
        taperGroup: q('[data-nd-taper-group]'), tapers: q('[data-nd-tapers]'),
        taperChips: q('[data-nd-taper-chips]'),
        config: q('[data-nd-config]'), proof: q('[data-nd-proof]'),
        qty: q('[data-nd-qty]'), qtyLabel: q('[data-nd-qty-label]'),
        minus: q('[data-nd-minus]'), plus: q('[data-nd-plus]'),
        add: q('[data-nd-add]'), live: q('[data-nd-live]'), close: q('.mf-nd__close')
      };

      var tick = '<svg class="mf-nd__tick" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12.5l5.2 5.2L20 6.8"/></svg>';
      this.ui.proof.innerHTML = PROOF.map(function (t) {
        return '<li>' + tick + '<span>' + esc(t) + '</span></li>';
      }).join('');

      this.buildReference();

      var self = this;

      r.addEventListener('click', function (e) {
        if (self.under) return;
        if (e.target.closest('[data-mf-nd-close]')) self.close();
      });

      document.addEventListener('keydown', function (e) {
        if (!self.isOpen()) return;
        /* While the cart is on top it owns the keyboard. */
        if (self.under) return;
        if (e.key === 'Escape') { e.preventDefault(); self.close(); }
        else if (e.key === 'Tab') self.trap(e);
      });

      this.ui.minus.addEventListener('click', function () { self.bumpQty(-1); });
      this.ui.plus.addEventListener('click', function () { self.bumpQty(1); });

      /* An artist buying twelve boxes should be able to say twelve,
         not press + eleven times. The figure is a real input and the
         buttons are still there for one more, one less.

         Nothing is corrected WHILE they type. Clearing the box to
         type 12 passes through empty, and through 1 on the way, and
         neither is a number they meant; snapping the value back at
         each keystroke is what makes a box like this unusable. Only
         the characters are policed on the way in. The value is
         settled when they commit - Enter, or leaving the box - and
         Add to cart reads the box itself, so a number typed and never
         blurred still counts. */
      this.ui.qty.addEventListener('input', function () {
        var digits = self.ui.qty.value.replace(/[^0-9]/g, '');
        if (digits !== self.ui.qty.value) self.ui.qty.value = digits;
        /* Buttons follow what is in the box, but an empty box is not
           yet a quantity, so neither button is disabled on it. */
        self.ui.minus.disabled = digits !== '' && parseInt(digits, 10) <= MIN_QTY;
        self.ui.plus.disabled = digits !== '' && parseInt(digits, 10) >= MAX_QTY;
      });

      this.ui.qty.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        self.setQty(self.readQty());
      });

      this.ui.qty.addEventListener('blur', function () { self.setQty(self.readQty()); });
      this.ui.qty.addEventListener('focus', function () {
        self.ui.qty.select();
        /* The keyboard animates in over the next few hundred
           milliseconds, and it takes the bottom of the screen with it.
           Re-measuring twice keeps Add to cart above it rather than
           behind it. */
        setTimeout(sizeToViewport, 300);
        setTimeout(sizeToViewport, 700);
      });
      this.ui.qty.addEventListener('blur', function () { setTimeout(sizeToViewport, 300); });
      this.ui.add.addEventListener('click', function () { self.addToCart(); });

      this.ui.gauge.addEventListener('click', function (e) {
        var b = e.target.closest('button[data-value]');
        if (!b || b.disabled) return;
        self.sel.gauge = b.getAttribute('data-value');
        self.sel.grouping = null;
        self.sel.taper = null;
        self.render();
      });

      this.ui.grouping.addEventListener('click', function (e) {
        var b = e.target.closest('button[data-value]');
        if (!b || b.disabled) return;
        self.sel.grouping = b.getAttribute('data-value');
        self.render();
      });

      /* Only ever reachable on a family that genuinely offers more
         than one taper for the configuration in hand. */
      this.ui.taperChips.addEventListener('click', function (e) {
        var b = e.target.closest('button[data-value]');
        if (!b || b.disabled) return;
        self.sel.taper = b.getAttribute('data-value');
        self.render();
      });

      this.ui.simple.addEventListener('click', function (e) {
        var b = e.target.closest('button[data-value][data-opt]');
        if (!b || b.disabled) return;
        self.sel.simple[b.getAttribute('data-opt')] = b.getAttribute('data-value');
        self.render();
      });

      /* The Smart Cart announces itself, so the drawer knows when to
         step underneath and when to come back. Bound once. */
      document.addEventListener('mf:cart:open', function () { self.setUnder(true); });
      document.addEventListener('mf:cart:close', function () { self.setUnder(false); });
    },

    /* ----- education, drawn once ----- */
    buildReference: function () {
      this.ui.gaugeDots.innerHTML = GAUGES.map(function (g) {
        /* Sized from the real needle diameters, lifted off a baseline
           so the steps stay obvious at this scale rather than
           collapsing into four near-identical dots. */
        var px = 13 + (g.relative - 0.20) * 104;
        return '<span class="mf-nd__dot-cell" data-gauge="' + parseInt(g.label, 10) + '">' +
          '<span class="mf-nd__dot-slot"><span class="mf-nd__dot" style="width:' + px.toFixed(1) + 'px;height:' + px.toFixed(1) + 'px"></span></span>' +
          '<span class="mf-nd__dot-label">' + esc(g.label) + '</span>' +
        '</span>';
      }).join('');

      var longest = 0;
      TAPERS.forEach(function (t) { if (t.length > longest) longest = t.length; });

      this.ui.tapers.innerHTML = TAPERS.map(function (t) {
        /* Every needle is drawn to the SAME overall length, so the
           shaft is the constant reference and nothing is stretched.
           The narrowing section alone is drawn in true proportion to
           the taper length, which puts M, L and XL at 3.5 : 5.5 : 7 -
           XL's taper is exactly twice M's, and you can see it without
           reading a number. A faint mark sits where the shaft stops
           being straight, which is where the taper is measured from. */
        var W = 116, tip = 112, start = 4, maxCone = 92;
        var cone = (t.length / longest) * maxCone;
        var shaftEnd = +(tip - cone).toFixed(1);
        var d = 'M' + start + ',8.8 L' + shaftEnd + ',8.8 L' + tip + ',12 L' +
                shaftEnd + ',15.2 L' + start + ',15.2 Z';
        return '<span class="mf-nd__taper" data-taper="' + esc(t.key) + '">' +
          '<svg class="mf-nd__taper-art" viewBox="0 0 ' + W + ' 24" preserveAspectRatio="xMidYMid meet" focusable="false">' +
            '<path class="mf-nd__taper-shape" d="' + d + '"/>' +
            '<line class="mf-nd__taper-mark" x1="' + shaftEnd + '" y1="5" x2="' + shaftEnd + '" y2="19"/>' +
          '</svg>' +
          '<span class="mf-nd__taper-key">' + esc(t.key) + '</span>' +
          '<span class="mf-nd__taper-mm">' + esc(t.mm) + '</span>' +
        '</span>';
      }).join('');
    },

    trap: function (e) {
      var f = this.panel.querySelectorAll('button:not([disabled]), a[href], input');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    },

    /* ----- data ----- */

    fetchProduct: function (handle) {
      if (this.cache[handle]) return Promise.resolve(this.cache[handle]);
      if (this.inflight[handle]) return this.inflight[handle];
      var self = this;
      var p = fetch(ROOT + '/products/' + handle + '.js', {
        headers: { Accept: 'application/json' }, credentials: 'same-origin'
      })
        .then(function (res) { if (!res.ok) throw new Error('status ' + res.status); return res.json(); })
        .then(function (json) { self.cache[handle] = json; delete self.inflight[handle]; return json; })
        .catch(function (err) { delete self.inflight[handle]; throw err; });
      this.inflight[handle] = p;
      return p;
    },

    prefetch: function (handle) {
      if (!handle) return;
      this.build();
      this.fetchProduct(handle).catch(function () {});
    },

    /* ----- open / close / layering ----- */

    lock: function () { document.documentElement.classList.add('mf-nd-locked'); },
    unlock: function () { document.documentElement.classList.remove('mf-nd-locked'); },

    /* Everything that says "the cart is on top" cleared in one place,
       so no half-state can survive a close, a re-open, or a cart that
       opened while this drawer was still animating shut. */
    clearUnder: function () {
      this.under = false;
      if (!this.root) return;
      this.root.removeAttribute('data-under');
      this.panel.removeAttribute('inert');
      this.panel.removeAttribute('aria-hidden');
    },

    open: function (meta) {
      if (!meta || !meta.handle) return;
      this.build();
      this.clearUnder();
      this.meta = meta;
      this.lastFocus = document.activeElement;

      this.ui.code.hidden = !meta.code;
      this.ui.code.textContent = meta.code || '';
      this.ui.strapline.textContent = meta.strapline || '';
      this.ui.title.textContent = meta.name || '';
      /* Clearance products do not get a link to a product page we do
         not want anyone reading. They get the reason instead, which is
         the one thing worth knowing about the OG Collection. */
      var clearance = isClearance(meta.handle) && CLEARANCE;
      this.ui.full.hidden = !!clearance;
      this.ui.clear.hidden = !clearance;
      this.ui.clear.textContent = clearance ? CLEARANCE : '';
      if (!clearance) this.ui.full.href = this.pdpHref(meta);

      this.ui.loading.hidden = false;
      this.ui.error.hidden = true;
      this.ui.main.hidden = true;
      this.ui.add.disabled = true;
      this.ui.add.textContent = 'Add to cart';
      this.setQty(MIN_QTY);
      this.product = null;
      this.sel = { gauge: null, grouping: null, taper: null, simple: {} };

      this.show();

      var self = this, handle = meta.handle;
      this.fetchProduct(handle)
        .then(function (product) {
          if (!self.meta || self.meta.handle !== handle) return;
          self.setProduct(product);
        })
        .catch(function () {
          if (!self.meta || self.meta.handle !== handle) return;
          self.ui.loading.hidden = true;
          self.ui.error.hidden = false;
        });
    },

    /* Sends customers to the NEW redesign product page via the safe
       ?view= route. Falls back to the normal product URL for anything
       without a redesign page yet. */
    pdpHref: function (meta) {
      var base = ROOT + '/products/' + meta.handle;
      var view = PDP_VIEW[meta.handle];
      return view ? base + '?view=' + view : (meta.url || base);
    },

    show: function () {
      var r = this.root, close = this.ui.close;
      r.setAttribute('data-open', '');
      this.lock();
      bindViewport();
      sizeToViewport();
      /* Measured again once the panel has landed and once more after
         that, because a preview bar can arrive late and the toolbars
         settle on their own schedule. */
      requestAnimationFrame(function () {
        r.setAttribute('data-shown', '');
        sizeToViewport();
      });
      setTimeout(sizeToViewport, 360);
      setTimeout(sizeToViewport, 1200);
      setTimeout(function () { if (close) close.focus(); }, 60);
    },

    close: function () {
      if (!this.root) return;
      var self = this, r = this.root;
      this.clearUnder();
      r.removeAttribute('data-shown');
      this.unlock();
      this.meta = null;
      /* Cleared again on landing: if the cart happened to open while
         this was animating shut, the drawer must not be left marked
         as sitting underneath it. */
      setTimeout(function () {
        r.removeAttribute('data-open');
        self.clearUnder();
        releaseViewport();
      }, 320);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    },

    isOpen: function () {
      return !!(this.root && this.root.hasAttribute('data-open'));
    },

    /* The Smart Cart sits above the drawer. While it is there the
       drawer is inert: not focusable, not clickable, not listening for
       Escape - but still on screen and still holding the artist's
       configuration, so closing the cart puts them straight back. */
    setUnder: function (on) {
      if (!this.root) return;
      if (on && !this.isOpen()) return;
      if (this.under === !!on) return;

      if (on) {
        this.under = true;
        this.root.setAttribute('data-under', '');
        this.panel.setAttribute('inert', '');
        this.panel.setAttribute('aria-hidden', 'true');
        return;
      }

      this.clearUnder();
      if (!this.isOpen()) return;

      /* Rebuy releases its own scroll lock as it closes, so ours is
         re-asserted after it has finished. */
      this.lock();
      var self = this;
      setTimeout(function () {
        if (self.isOpen() && !self.under) {
          self.lock();
          var el = self.ui.add;
          if (el && !el.disabled) el.focus();
        }
      }, 340);
    },

    /* ----- model ----- */

    /* Everything downstream works from this list, so a configuration
       we do not carry simply does not exist as far as the drawer is
       concerned. Out of stock is a different thing and stays visible. */
    carried: function () {
      return (this.product.variants || []).filter(function (v) {
        return NOT_CARRIED.indexOf(v.sku) === -1;
      });
    },

    setProduct: function (product) {
      this.product = product;
      if (!this.ui.title.textContent) this.ui.title.textContent = product.title || '';

      var names = optionNames(product);
      this.idx = {
        gauge: indexOfOption(names, 'gauge'),
        grouping: indexOfOption(names, 'grouping'),
        taper: indexOfOption(names, 'taper')
      };

      this.mode = (this.idx.gauge > -1 || this.idx.grouping > -1 || this.idx.taper > -1)
        ? 'cartridge' : 'simple';

      var pool = this.carried();

      if (this.mode === 'cartridge') {
        if (this.idx.grouping === -1) this.idx.grouping = names.length > 1 ? 1 : 0;
      } else {
        /* Only options this product actually varies on. A single-value
           option, or Shopify's Title placeholder, is not a choice. */
        this.simpleIdx = [];
        for (var i = 0; i < names.length; i++) {
          if (String(names[i]).trim().toLowerCase() === 'title') continue;
          if (uniq(pool.map(function (v) { return variantOption(v, i); })).length > 1) {
            this.simpleIdx.push(i);
          }
        }
      }

      var first = null;
      for (var j = 0; j < pool.length; j++) {
        if (pool[j].available) { first = pool[j]; break; }
      }
      if (!first) first = pool[0];

      if (first) {
        if (this.mode === 'cartridge') {
          this.sel.gauge = this.idx.gauge > -1 ? variantOption(first, this.idx.gauge) : null;
          this.sel.grouping = variantOption(first, this.idx.grouping);
          this.sel.taper = this.idx.taper > -1 ? variantOption(first, this.idx.taper) : null;
        } else {
          var self = this;
          this.simpleIdx.forEach(function (k) {
            self.sel.simple[k] = variantOption(first, k);
          });
        }
      }

      /* Cartridges are sold by the box, everything else by the item,
         and the caption beside Add to cart says which. */
      var boxes = this.mode === 'cartridge';
      this.ui.qtyLabel.textContent = boxes ? 'Boxes' : 'Qty';
      this.ui.qty.setAttribute('aria-label', boxes ? 'Number of boxes' : 'Quantity');

      this.ui.unit.hidden = !(this.mode === 'cartridge' && UNIT);
      this.ui.unit.textContent = UNIT;
      this.ui.cartridge.hidden = this.mode !== 'cartridge';
      this.ui.simple.hidden = this.mode === 'cartridge';

      this.ui.loading.hidden = true;
      this.ui.error.hidden = true;
      this.ui.main.hidden = false;
      this.render();
    },

    gaugeValues: function () {
      if (this.idx.gauge === -1) return [];
      var i = this.idx.gauge;
      return uniq(this.carried().map(function (v) { return variantOption(v, i); })).sort(byNumber);
    },

    variantsForGauge: function () {
      var self = this, pool = this.carried();
      if (this.idx.gauge === -1) return pool;
      return pool.filter(function (v) {
        return variantOption(v, self.idx.gauge) === self.sel.gauge;
      });
    },

    /* The tapers this exact gauge and grouping is made in.

       Almost always one, and where it is one nothing about the drawer
       changes. The OG Curved Magnums in 10 gauge and the OG Hollow
       Round Liners come in two, M and L, against the same grouping -
       which is why those families used to show the same number twice
       in the grouping row with no way to tell the two apart. */
    tapersForGrouping: function () {
      if (this.idx.taper === -1) return [];
      var self = this;
      return uniq(this.variantsForGauge()
        .filter(function (v) {
          return variantOption(v, self.idx.grouping) === self.sel.grouping;
        })
        .map(function (v) { return variantOption(v, self.idx.taper); }))
        .sort(function (a, b) { return taperRank(a) - taperRank(b); });
    },

    currentVariant: function () {
      var self = this;

      if (this.mode === 'simple') {
        var pool = this.carried();
        for (var i = 0; i < pool.length; i++) {
          var ok = true;
          for (var k = 0; k < this.simpleIdx.length; k++) {
            var n = this.simpleIdx[k];
            if (variantOption(pool[i], n) !== this.sel.simple[n]) { ok = false; break; }
          }
          if (ok) return pool[i];
        }
        return null;
      }

      var matches = this.variantsForGauge().filter(function (v) {
        return variantOption(v, self.idx.grouping) === self.sel.grouping;
      });
      if (!matches.length) return null;

      /* With one taper this is the same first match it always was. */
      if (this.idx.taper > -1 && this.sel.taper) {
        for (var j = 0; j < matches.length; j++) {
          if (variantOption(matches[j], self.idx.taper) === self.sel.taper) return matches[j];
        }
      }
      return matches[0];
    },

    /* ----- render ----- */

    chip: function (value, label, pressed, available, kind, optIndex) {
      var attrs = ' class="mf-nd__chip" data-value="' + esc(value) + '"';
      if (optIndex != null) attrs += ' data-opt="' + optIndex + '"';
      attrs += ' aria-pressed="' + (pressed ? 'true' : 'false') + '"';
      if (!available) {
        attrs += ' disabled aria-label="' + esc(kind ? kind + ' ' + label : label) + ', out of stock"';
      }
      return '<button type="button"' + attrs + '>' + esc(label) + '</button>';
    },

    render: function () {
      var p = this.product;
      if (!p) return;

      if (this.mode === 'cartridge') this.renderCartridge();
      else this.renderSimple();

      var variant = this.currentVariant();

      var img = (variant && variant.featured_image && variant.featured_image.src) ||
        p.featured_image || (p.images && p.images[0]);
      if (img) {
        this.ui.image.src = String(img).replace(/(\.[a-z]+)(\?|$)/i, '_400x$1$2');
        this.ui.image.alt = p.title;
      }

      this.ui.price.textContent = money(variant ? variant.price : p.price);
      this.ui.currency.textContent = currencyCode();

      var addable = !!(variant && variant.available);
      this.ui.add.disabled = !addable || this.busy;
      this.ui.add.textContent = this.busy ? 'Adding' : (addable ? 'Add to cart' : 'Sold out');
    },

    renderCartridge: function () {
      var self = this;
      var all = this.carried();
      var gauges = this.gaugeValues();

      /* Gauge is shown on EVERY family, including the ones that only
         come in one. With a single gauge there is nothing to choose,
         so it is stated as a specification rather than dressed up as
         a control that does nothing. */
      this.ui.gaugeGroup.hidden = gauges.length === 0;

      if (gauges.length > 1) {
        this.ui.gauge.hidden = false;
        this.ui.gaugeFixed.hidden = true;
        this.ui.gauge.innerHTML = gauges.map(function (g) {
          var anyLive = all.some(function (v) {
            return variantOption(v, self.idx.gauge) === g && v.available;
          });
          return self.chip(g, gaugeLabel(g), g === self.sel.gauge, anyLive, 'Gauge');
        }).join('');
      } else if (gauges.length === 1) {
        this.sel.gauge = gauges[0];
        this.ui.gauge.hidden = true;
        this.ui.gauge.innerHTML = '';
        this.ui.gaugeFixed.hidden = false;
        var mm = gaugeMm(gauges[0]);
        this.ui.gaugeFixed.innerHTML =
          '<b>' + esc(gaugeLabel(gauges[0])) + '</b>' +
          (mm ? '<i>' + esc(mm) + '</i>' : '') +
          '<span class="mf-nd__fixed-note">Only gauge in this family</span>';
      }

      var pool = this.variantsForGauge();
      var anyOOS = false;

      /* One chip per grouping, not one per variant.

         Where a grouping comes in two tapers the old loop drew that
         number twice, identical and indistinguishable, and only the
         first of the two could ever be reached. The number is drawn
         once now, and the taper is chosen on its own row underneath.
         On every family that has a single taper this produces exactly
         the same chips it always did. */
      var groupings = uniq(pool.map(function (v) {
        return variantOption(v, self.idx.grouping);
      })).sort(byNumber);

      this.ui.grouping.innerHTML = groupings.map(function (val) {
        var live = pool.some(function (v) {
          return variantOption(v, self.idx.grouping) === val && v.available;
        });
        if (!live) anyOOS = true;
        return self.chip(val, val, val === self.sel.grouping, live, 'Grouping');
      }).join('');

      this.ui.oos.hidden = !anyOOS;

      /* The taper row appears only where there is a choice to make.
         One taper, or none, and it is not drawn at all and the
         education below carries on doing what it always did. */
      function taperLive(t) {
        return pool.some(function (v) {
          return variantOption(v, self.idx.grouping) === self.sel.grouping &&
            variantOption(v, self.idx.taper) === t && v.available;
        });
      }

      var tapers = this.tapersForGrouping();

      if (tapers.length > 1) {
        /* Keep the artist's taper across a change of grouping - but only
           while it is one they can actually buy.

           Holding on to a sold-out taper is what made grouping 15 on the
           10 gauge Curved Magnums unselectable. 15 comes in M and L; the
           L is sold out. Arriving from 13, where L is the live one, the
           taper was kept, the variant came back unavailable, and the
           recovery below threw the whole grouping away and bounced
           straight back to 13. Moving to the taper that is in stock
           keeps the grouping the artist actually asked for.

           If nothing in the grouping is in stock the recovery still
           takes over, which is right: that grouping's chip is crossed
           out and cannot be clicked in the first place. */
        var liveTapers = tapers.filter(taperLive);
        var keep = tapers.indexOf(this.sel.taper) > -1 &&
          (!liveTapers.length || liveTapers.indexOf(this.sel.taper) > -1);
        if (!keep) {
          this.sel.taper = liveTapers[0] || tapers[0];
        }
        this.ui.taperChips.hidden = false;
        this.ui.taperChips.innerHTML = tapers.map(function (t) {
          return self.chip(t, taperKey(t), t === self.sel.taper, taperLive(t), 'Taper');
        }).join('');
      } else {
        this.sel.taper = tapers.length === 1 ? tapers[0] : null;
        this.ui.taperChips.hidden = true;
        this.ui.taperChips.innerHTML = '';
      }

      var variant = this.currentVariant();
      if (!variant || !variant.available) {
        var live = pool.filter(function (v) { return v.available; })[0];
        if (live) {
          this.sel.grouping = variantOption(live, this.idx.grouping);
          if (this.idx.taper > -1) this.sel.taper = variantOption(live, this.idx.taper);
          this.renderCartridge();
          return;
        }
      }
      variant = this.currentVariant();

      /* The reference strips follow the real selection. They never
         drive it, and on a fixed-gauge family the right circle simply
         lights up on its own. */
      var activeGauge = variant && this.idx.gauge > -1
        ? parseInt(variantOption(variant, this.idx.gauge), 10) : null;
      this.ui.gaugeDots.querySelectorAll('[data-gauge]').forEach(function (n) {
        n.classList.toggle('is-active', activeGauge !== null &&
          parseInt(n.getAttribute('data-gauge'), 10) === activeGauge);
      });

      this.ui.taperGroup.hidden = this.idx.taper === -1;
      var activeTaper = variant && this.idx.taper > -1
        ? taperKey(variantOption(variant, this.idx.taper)) : null;
      this.ui.tapers.querySelectorAll('[data-taper]').forEach(function (n) {
        n.classList.toggle('is-active', activeTaper !== null &&
          n.getAttribute('data-taper') === activeTaper);
      });

      if (variant) {
        var bits = [];
        if (this.idx.gauge > -1) bits.push(gaugeLabel(variantOption(variant, this.idx.gauge)));
        bits.push(variantOption(variant, this.idx.grouping));
        if (this.idx.taper > -1) bits.push(taperKey(variantOption(variant, this.idx.taper)));
        this.ui.config.textContent = bits.join(' / ');
      } else {
        this.ui.config.textContent = '-';
      }
    },

    /* Merch and anything else: one chip group per option the product
       genuinely varies on, and nothing else. */
    renderSimple: function () {
      var self = this;
      var pool = this.carried();
      var names = optionNames(this.product);
      var anyOOS = false;

      var html = this.simpleIdx.map(function (i) {
        var values = uniq(pool.map(function (v) { return variantOption(v, i); }));
        var chips = values.map(function (val) {
          /* Available means: some variant with this value, matching
             every other current choice, is in stock. */
          var live = pool.some(function (v) {
            if (variantOption(v, i) !== val || !v.available) return false;
            for (var k = 0; k < self.simpleIdx.length; k++) {
              var n = self.simpleIdx[k];
              if (n !== i && variantOption(v, n) !== self.sel.simple[n]) return false;
            }
            return true;
          });
          if (!live) anyOOS = true;
          return self.chip(val, val, val === self.sel.simple[i], live, names[i], i);
        }).join('');

        return '<div class="mf-nd__group">' +
          '<p class="mf-nd__label"><span>' + esc(names[i]) + '</span></p>' +
          '<div class="mf-nd__chips" role="group" aria-label="' + esc(names[i]) + '">' + chips + '</div>' +
        '</div>';
      }).join('');

      if (anyOOS) {
        html += '<p class="mf-nd__note mf-nd__note--loose">Crossed out options are out of stock.</p>';
      }

      this.ui.simple.innerHTML = html;

      var variant = this.currentVariant();
      if ((!variant || !variant.available) && this.simpleIdx.length) {
        var liveVariant = pool.filter(function (v) { return v.available; })[0];
        if (liveVariant) {
          var changed = false;
          this.simpleIdx.forEach(function (n) {
            var val = variantOption(liveVariant, n);
            if (self.sel.simple[n] !== val) { self.sel.simple[n] = val; changed = true; }
          });
          if (changed) { this.renderSimple(); return; }
        }
      }
    },

    /* What the box actually means, in one place. Blank, zero, a
       pasted word, a negative sign: all of it comes back as one. */
    readQty: function () {
      var n = parseInt(String(this.ui.qty.value).replace(/[^0-9]/g, ''), 10);
      if (isNaN(n) || n < MIN_QTY) return MIN_QTY;
      return n > MAX_QTY ? MAX_QTY : n;
    },

    setQty: function (n) {
      this.ui.qty.value = n;
      this.ui.minus.disabled = n <= MIN_QTY;
      this.ui.plus.disabled = n >= MAX_QTY;
    },

    bumpQty: function (delta) {
      this.setQty(Math.max(MIN_QTY, Math.min(MAX_QTY, this.readQty() + delta)));
    },

    /* ----- add to cart, then hand over the top layer ----- */

    addToCart: function () {
      var self = this;
      if (this.busy) return;
      var variant = this.currentVariant();
      if (!variant || !variant.available) return;

      var qty = this.readQty();

      this.busy = true;
      this.render();

      /* The drawer is deliberately NOT closed. MFCart.open() makes the
         Smart Cart slide over it, the drawer steps underneath, and the
         configuration is still here when the cart closes. */
      window.MFCart.add(variant.id, qty)
        .then(function () {
          self.ui.live.textContent = 'Added to cart.';
          self.setQty(MIN_QTY);
        })
        .catch(function (err) {
          self.ui.live.textContent = 'Could not add to cart.';
          if (window.console) console.warn('[MF drawer]', err);
        })
        .then(function () {
          self.busy = false;
          if (self.isOpen()) self.render();
        });
    }
  };

  /* ---------- delegated triggers ----------

     Every card in the range funnels through here:

       [data-mf-drawer]                   anything, explicitly
       quick-view-product [data-href]     EVERY quick-view button
       #shop-cartridges [data-href]       the new cartridge tiles
       [data-mf-cartridge] [data-href]    the same tiles elsewhere
       .mf-og__card[data-href]            OG Collection and Merch cards
       [data-js-product-add-to-cart]      the theme's own product cards

     The second line is the one that matters. Every redesign section
     that offers a choice - the Shop All tiles, the OG cards, KEEP
     SHOPPING, and CHOOSE OPTIONS on the six cartridge product pages -
     wraps its button in the theme's <quick-view-product> element. The
     first four had been listed here one by one and the PDP button and
     the KEEP SHOPPING tiles had not, so those two fell through to the
     theme's own quick view: the old options UI and the old cart, on
     the redesign's own product pages. Matching the element itself
     means there is one cart route and no list to keep adding to.

     The listener runs in the capture phase and stops propagation, so
     the theme's ?view=quick-view handler on the same button never
     fires. That system stays installed, just unused for these cards. */

  var TRIGGERS = [
    '[data-mf-drawer]',
    'quick-view-product [data-href]',
    '#shop-cartridges [data-href]',
    '[data-mf-cartridge] [data-href]',
    '.mf-og__card[data-href]',
    '[data-js-product-add-to-cart][data-href]'
  ].join(', ');

  function handleFromHref(href) {
    if (!href) return '';
    var path = String(href).split('?')[0].split('#')[0].replace(/\/$/, '');
    var bits = path.split('/products/');
    return bits.length > 1 ? bits[1] : '';
  }

  function metaFrom(node) {
    if (node.hasAttribute('data-mf-drawer')) {
      return {
        handle: node.getAttribute('data-mf-drawer'),
        code: node.getAttribute('data-mf-code') || '',
        name: node.getAttribute('data-mf-name') || '',
        strapline: node.getAttribute('data-mf-strapline') || '',
        url: node.getAttribute('data-mf-url') || ''
      };
    }

    var href = node.getAttribute('data-href') || '';
    var handle = handleFromHref(href);

    /* The redesign cartridge tiles carry a code, name and strapline
       worth reusing. Every other card just gives us the handle, and
       the drawer fills in the title from the product itself. */
    var codeEl = node.querySelector('.mf-cart-types__code');
    var titleEl = node.querySelector('.mf-cart-types__title');
    var metaEl = node.querySelector('.mf-cart-types__meta');
    if (!codeEl && !titleEl) return { handle: handle, code: '', name: '', strapline: '', url: href };

    var code = codeEl ? codeEl.textContent.trim() : '';
    var name = titleEl ? titleEl.textContent.trim() : '';
    if (code && name.indexOf(code) === 0) name = name.slice(code.length).trim();
    var strapline = metaEl ? metaEl.textContent.trim().split('•')[0].trim() : '';
    return { handle: handle, code: code, name: name, strapline: strapline, url: href };
  }

  function trigger(node) {
    if (!node || !node.closest) return null;
    if (node.closest('#MFNeedleDrawer')) return null;
    return node.closest(TRIGGERS);
  }

  /* One variant and nothing to choose: opening a drawer to show a
     single button would be friction for its own sake, so it goes
     straight into the cart. */
  function openOrAdd(meta, node) {
    Drawer.build();
    return Drawer.fetchProduct(meta.handle)
      .then(function (product) {
        var pool = (product.variants || []).filter(function (v) {
          return NOT_CARRIED.indexOf(v.sku) === -1;
        });
        if (!nothingToChoose(product, pool) || !pool.length || !pool[0].available) {
          Drawer.open(meta);
          return;
        }
        if (node) node.setAttribute('aria-busy', 'true');
        return window.MFCart.add(pool[0].id, 1)
          .catch(function (err) { if (window.console) console.warn('[MF drawer]', err); })
          .then(function () { if (node) node.removeAttribute('aria-busy'); });
      })
      .catch(function () { Drawer.open(meta); });
  }

  document.addEventListener('click', function (e) {
    var t = trigger(e.target);
    if (!t) return;
    var meta = metaFrom(t);
    if (!meta.handle) return;
    e.preventDefault();
    e.stopPropagation();
    openOrAdd(meta, t);
  }, true);

  ['pointerenter', 'touchstart', 'focusin'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var t = trigger(e.target);
      if (!t) return;
      Drawer.prefetch(t.getAttribute('data-mf-drawer') || handleFromHref(t.getAttribute('data-href')));
    }, { capture: true, passive: true });
  });

  window.MFNeedleDrawer = {
    open: function (meta) { Drawer.open(meta); },
    prefetch: function (h) { Drawer.prefetch(h); },
    close: function () { Drawer.close(); },
    isOpen: function () { return Drawer.isOpen(); },
    state: Drawer
  };
})();
